import { t } from '@lingui/macro'
import React, { useCallback, useMemo, useState } from 'react'
import { Mention, MentionsInput, SuggestionDataItem } from 'react-mentions'
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar'
import { Maximize2, Minimize2 } from 'lucide-react'
import { cn } from '../../lib/utils'

export const userMarkup = '[__display__](user:__id__)'

// everyone 멘션을 사용자 언어로 번역
function getDisplayName(id: string, display: string): string {
  if (id === 'everyone' && display === 'everyone') {
    return t`Everyone`;
  }
  return display;
}

interface IMentionTextArea {
  className?: string,
  text: string,
  placeholder: string,
  suggestions: SuggestionDataItem[],
  onChange: (text: string) => void,
  onSubmit: ((text: string) => void),
  /** 멀티라인 최소 높이(px). 미지정 시 기존(약 1줄) 유지 — 코멘트용 */
  minHeight?: number,
}
const MentionTextArea: React.FC<IMentionTextArea> = ({ className, text, placeholder, suggestions, onChange, onSubmit, minHeight }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const changed = useCallback((e: { target: { value: string } }) => {
    onChange(e.target.value);
  }, [onChange]);

  // 줄 수 계산 (줄바꿈 + 긴 텍스트 줄바꿈 추정)
  const lineCount = text.split('\n').length;
  const showExpandButton = lineCount >= 10 || isExpanded;

  // 10줄 = 약 210px (line-height 1.5 * 14px = 21px per line)
  const maxHeight = isExpanded ? '400px' : '210px';

  // 🔧 style 객체를 메모이제이션하여 react-mentions의 불필요한 DOM 갱신 방지
  const minH = minHeight ? `${minHeight}px` : undefined;
  const mergedStyle = useMemo(() => ({
    ...mentionStyle,
    '&multiLine': {
      ...mentionStyle['&multiLine'],
      control: {
        ...mentionStyle['&multiLine'].control,
        maxHeight: maxHeight,
        overflow: 'auto',
        ...(minH ? { minHeight: minH } : {}),
      },
      input: {
        ...mentionStyle['&multiLine'].input,
        maxHeight: maxHeight,
        overflow: 'auto !important',
        ...(minH ? { minHeight: minH } : {}),
      },
      highlighter: {
        ...mentionStyle['&multiLine'].highlighter,
        maxHeight: maxHeight,
        overflow: 'hidden',
        ...(minH ? { minHeight: minH } : {}),
      },
    },
  }), [maxHeight, minH]);

  const keyDownHandler = useCallback((e: React.KeyboardEvent) => {
    if (!e.nativeEvent.isComposing && e.key == 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSubmit(text);
    }
  }, [onSubmit, text]);

  return (
    <div className={cn("mention-textarea relative w-full", className)}>
      <MentionsInput
        value={text}
        onChange={changed}
        onKeyDown={keyDownHandler}
        className="nextpod-mentions"
        style={mergedStyle}
        placeholder={placeholder}
        a11ySuggestionsListLabel={"Suggested mentions"}
        allowSuggestionsAboveCursor={true}
        data-cy="text"
      >
        <Mention
          markup={userMarkup}
          trigger="@"
          appendSpaceOnAdd={true}
          displayTransform={(id, display) => `@${getDisplayName(id, display)}`}
          data={suggestions}
          renderSuggestion={(suggestion: any, search, highlightedDisplay, index, focused) => (
            <div className="flex items-center gap-2">
              <Avatar className="w-6 h-6">
                <AvatarImage src={suggestion.avatar} />
                <AvatarFallback className="text-meta-sm">{getDisplayName(suggestion.id, suggestion.display)?.slice(0, 1).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="font-medium text-sm">{getDisplayName(suggestion.id, suggestion.display)}</span>
            </div>
          )}
          style={{
            backgroundColor: 'rgb(var(--mention-highlight))',
            borderRadius: '2px',
            color: 'transparent',
          }}
        />
      </MentionsInput>

      {/* 확장/축소 버튼 - 10줄 이상일 때만 표시 */}
      {showExpandButton && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="absolute top-1 right-1 p-1 rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
          title={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
        </button>
      )}
    </div>
  )
}

export default MentionTextArea;

// https://github.com/signavio/react-mentions/blob/master/demo/src/examples/defaultStyle.js
const mentionStyle = {
  control: {
    backgroundColor: 'transparent',
    fontSize: '14px',
    lineHeight: '1.5',
    fontFamily: 'inherit',
    fontWeight: '400',
  },
  '&multiLine': {
    control: {
      minHeight: '20px',
    },
    highlighter: {
      padding: '0 0 0 4px',  // input과 동일
      border: 'none',
      color: 'transparent',
      wordBreak: 'break-word' as any,
      whiteSpace: 'pre-wrap',
    },
    input: {
      padding: '0 0 0 4px',  // 오른쪽 패딩 0 - 스크롤바가 보더에 붙음
      border: 'none',
      outline: 'none',
      overflow: 'auto !important',
      backgroundColor: 'transparent',
      color: 'inherit',
      wordBreak: 'break-word' as any,
      whiteSpace: 'pre-wrap',
      '&::placeholder': {
        color: 'rgb(var(--muted-foreground))',
        fontSize: '14px',
        fontWeight: '400',
        lineHeight: '1.5',
      },
    },
  },
  suggestions: {
    list: {
      backgroundColor: 'rgb(var(--popover))',
      border: '1px solid rgb(var(--border))',
      fontSize: '14px',
      borderRadius: '0.5rem', // rounded-lg
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // shadow-md
      zIndex: 9999,
      bottom: '100%',
      top: 'auto',
      marginBottom: '8px',
      padding: '4px',
      maxHeight: '220px',
      overflow: 'auto',
    },
    item: {
      padding: '6px 8px',
      borderRadius: '4px', // rounded-md
      color: 'rgb(var(--popover-foreground))',
      '&focused': {
        backgroundColor: 'rgb(var(--accent))',
        color: 'rgb(var(--accent-foreground))',
      },
    },
  }
};

// function getMentionables(users: User[]): SuggestionDataItem[] {
//   return users.map(u => {
//     return { id: u.id, display: u.name } as SuggestionDataItem;
//   });
// }

function getCommands(): SuggestionDataItem[] {
  return ['/dalle', '/gpt'].map(u => {
    return { id: u, display: u } as SuggestionDataItem;
  });
}